<template>
  <div class="dossier-hr">
    <div class="dossier-hr__wrapper">
      <!-- ─── Cabeçalho colapsável ─── -->
      <div class="dossier-hr__header" @click="toggleCollapse">
        <div class="dossier-hr__header-left">
          <span class="dossier-hr__state-icon" :class="headerStateClass">
            <i :class="headerStateIcon"></i>
          </span>
          <div>
            <h3 class="dossier-hr__title">
              Recursos Humanos – Colaboradores CLT
            </h3>
            <span v-if="isCollapsed" class="dossier-hr__subtitle">
              {{ collaboratorSummary || "Carregando colaboradores..." }}
            </span>
          </div>
        </div>
        <button
          class="dossier-hr__toggle"
          type="button"
          :aria-expanded="!isCollapsed"
        >
          <i
            class="fas"
            :class="isCollapsed ? 'fa-chevron-down' : 'fa-chevron-up'"
          ></i>
        </button>
      </div>

      <!-- ─── Corpo ─── -->
      <div v-show="!isCollapsed" class="dossier-hr__body">
        <!-- Explicativo -->
        <div v-if="!isViewMode" class="dossier-hr__explainer">
          <p>
            Lista os colaboradores com vínculo CLT ativos no sistema. Para cada
            colaborador, registre a situação dos documentos trabalhistas
            obrigatórios e anexe os comprovantes quando necessário.<br />
            Para adicionar ou editar colaboradores, acesse o módulo
            <strong>Usuários</strong>.
          </p>
        </div>

        <!-- ── Filtros e ações ── -->
        <div class="dossier-hr__toolbar">
          <v-text-field
            v-model="search"
            prepend-inner-icon="mdi-magnify"
            placeholder="Buscar colaborador"
            dense
            outlined
            hide-details
            clearable
            class="dossier-hr__search"
          />

          <v-select
            v-model="filterRole"
            :items="roleOptions"
            dense
            outlined
            hide-details
            placeholder="Todas as funções"
            clearable
            class="dossier-hr__filter-role"
          />

          <v-btn
            small
            class="btn-add"
            :loading="loadingCollaborators"
            @click="loadCollaborators"
          >
            <i class="fas fa-sync-alt mr-1"></i>
            Atualizar lista
          </v-btn>
        </div>

        <!-- ── Loading ── -->
        <b-overlay :show="loadingCollaborators" opacity="0.7">
          <!-- ── Lista de colaboradores ── -->
          <div v-if="filteredCollaborators.length > 0" class="dossier-hr__list">
            <div
              v-for="collaborator in filteredCollaborators"
              :key="collaborator.id"
              class="dossier-hr__item"
              :class="{
                'dossier-hr__item--expanded': expandedIds.includes(
                  collaborator.id
                ),
              }"
            >
              <!-- Linha resumo do colaborador -->
              <div
                class="dossier-hr__item-row"
                @click="toggleExpand(collaborator.id)"
              >
                <!-- Ícone de completude -->
                <span
                  class="dossier-hr__item-state"
                  :class="itemStateClass(collaborator.id)"
                >
                  <i :class="itemStateIcon(collaborator.id)"></i>
                </span>

                <!-- Foto / avatar -->
                <div class="dossier-hr__item-avatar">
                  <img
                    v-if="collaborator.photo_url"
                    :src="collaborator.photo_url"
                    :alt="collaborator.name"
                    class="dossier-hr__avatar-img"
                  />
                  <span v-else class="dossier-hr__avatar-initials">
                    {{ initials(collaborator.name) }}
                  </span>
                </div>

                <!-- Dados básicos -->
                <div class="dossier-hr__item-info">
                  <span class="dossier-hr__item-name">{{
                    collaborator.name
                  }}</span>
                  <span class="dossier-hr__item-meta">
                    {{ collaborator.office || "Sem cargo" }}
                    <template v-if="collaborator.cpf">
                      · {{ collaborator.cpf }}</template
                    >
                    <template
                      v-if="collaborator.professional_class_council_value"
                    >
                      · {{ collaborator.professional_class_council_value }}
                    </template>
                  </span>
                </div>

                <!-- Progresso de documentos -->
                <div class="dossier-hr__item-progress">
                  <span class="dossier-hr__progress-text">
                    {{ docProgress(collaborator.id) }}
                  </span>
                  <div class="dossier-hr__progress-bar">
                    <div
                      class="dossier-hr__progress-fill"
                      :style="{ width: docProgressPct(collaborator.id) + '%' }"
                      :class="progressClass(collaborator.id)"
                    ></div>
                  </div>
                </div>

                <!-- Seta -->
                <i
                  class="fas dossier-hr__item-chevron"
                  :class="
                    expandedIds.includes(collaborator.id)
                      ? 'fa-chevron-up'
                      : 'fa-chevron-down'
                  "
                >
                </i>
              </div>

              <!-- Painel de documentos expandido -->
              <div
                v-if="expandedIds.includes(collaborator.id)"
                class="dossier-hr__item-docs"
              >
                <div class="dossier-hr__docs-grid">
                  <div
                    v-for="docDef in documentDefinitions"
                    :key="docDef.key"
                    class="dossier-hr__doc-card"
                    :class="docCardClass(collaborator.id, docDef.key)"
                  >
                    <!-- Header do doc -->
                    <div class="dossier-hr__doc-header">
                      <span class="dossier-hr__doc-name">{{
                        docDef.label
                      }}</span>
                      <span
                        class="dossier-hr__doc-state-badge"
                        :class="docBadgeClass(collaborator.id, docDef.key)"
                      >
                        {{ docStateBadge(collaborator.id, docDef.key) }}
                      </span>
                    </div>

                    <!-- Status select -->
                    <div v-if="!isViewMode" class="dossier-hr__doc-field">
                      <label class="dossier-hr__doc-label">Situação</label>
                      <v-select
                        v-model="getDocData(collaborator.id, docDef.key).status"
                        :items="docDef.statusOptions"
                        dense
                        outlined
                        hide-details
                        class="dossier-hr__doc-select"
                      />
                    </div>
                    <div v-else class="dossier-hr__doc-view-status">
                      {{
                        getDocData(collaborator.id, docDef.key).status || "—"
                      }}
                    </div>

                    <!-- Validade (quando aplicável) -->
                    <div
                      v-if="docDef.hasValidity"
                      class="dossier-hr__doc-field"
                    >
                      <label class="dossier-hr__doc-label">Validade</label>
                      <v-text-field
                        v-if="!isViewMode"
                        v-model="
                          getDocData(collaborator.id, docDef.key).validity_date
                        "
                        type="date"
                        dense
                        outlined
                        hide-details
                        class="dossier-hr__doc-input"
                      />
                      <span v-else class="dossier-hr__doc-view-value">
                        {{
                          formatDateBR(
                            getDocData(collaborator.id, docDef.key)
                              .validity_date
                          )
                        }}
                      </span>
                    </div>

                    <!-- Upload / arquivo -->
                    <div class="dossier-hr__doc-field">
                      <div
                        v-if="getDocData(collaborator.id, docDef.key).file_url"
                      >
                        <a
                          :href="
                            getDocData(collaborator.id, docDef.key).file_url
                          "
                          target="_blank"
                          class="dossier-hr__doc-file-link"
                        >
                          <i class="fas fa-file-pdf mr-1"></i>
                          Ver documento
                        </a>
                        <v-btn
                          v-if="!isViewMode"
                          x-small
                          text
                          class="btn-notcertified ml-1"
                          @click="triggerDocUpload(collaborator.id, docDef.key)"
                        >
                          <i class="fas fa-sync-alt mr-1"></i>Substituir
                        </v-btn>
                      </div>
                      <v-btn
                        v-else-if="!isViewMode"
                        x-small
                        class="btn-add"
                        :loading="
                          getDocData(collaborator.id, docDef.key)._uploading
                        "
                        @click="triggerDocUpload(collaborator.id, docDef.key)"
                      >
                        <i class="fas fa-upload mr-1"></i>Anexar
                      </v-btn>
                      <span v-else class="dossier-hr__doc-empty"
                        >Não anexado</span
                      >
                    </div>

                    <!-- Input file oculto por colaborador+doc -->
                    <input
                      v-if="!isViewMode"
                      :ref="`upload_${collaborator.id}_${docDef.key}`"
                      type="file"
                      accept="application/pdf"
                      style="display: none"
                      @change="
                        (e) => handleDocUpload(e, collaborator.id, docDef.key)
                      "
                    />
                  </div>
                </div>

                <!-- Observações do colaborador -->
                <div class="dossier-hr__doc-obs">
                  <label class="dossier-hr__doc-label">Observações</label>
                  <v-textarea
                    v-if="!isViewMode"
                    v-model="getCollaboratorExtra(collaborator.id).observations"
                    dense
                    outlined
                    rows="2"
                    auto-grow
                    hide-details
                    placeholder="Informações complementares (opcional)"
                  />
                  <p
                    v-else-if="
                      getCollaboratorExtra(collaborator.id).observations
                    "
                    class="dossier-hr__doc-view-value"
                  >
                    {{ getCollaboratorExtra(collaborator.id).observations }}
                  </p>
                  <span v-else class="dossier-hr__doc-empty">—</span>
                </div>

                <!-- Salvar colaborador individual -->
                <div v-if="!isViewMode" class="dossier-hr__item-save">
                  <v-btn
                    small
                    class="btn-add"
                    :loading="savingIds.includes(collaborator.id)"
                    @click="saveCollaborator(collaborator.id)"
                  >
                    <i class="fas fa-save mr-1"></i>
                    Salvar {{ collaborator.name.split(" ")[0] }}
                  </v-btn>
                </div>
              </div>
            </div>
          </div>

          <!-- Estado vazio -->
          <div v-else-if="!loadingCollaborators" class="dossier-hr__empty">
            <i class="fas fa-users dossier-hr__empty-icon"></i>
            <p>Nenhum colaborador CLT ativo encontrado.</p>
            <p class="dossier-hr__empty-sub">
              Acesse o módulo <strong>Usuários</strong> para cadastrar
              colaboradores.
            </p>
          </div>
        </b-overlay>

        <!-- Salvar resposta condicional (se não há CLT) -->
        <div v-if="!isViewMode" class="dossier-hr__footer">
          <div class="dossier-hr__na-option">
            <label class="dossier-hr__na-label">
              <input
                type="checkbox"
                v-model="formData.no_clt_employees"
                class="dossier-hr__na-checkbox"
              />
              <span>A instituição não possui colaboradores CLT</span>
            </label>
          </div>

          <v-btn
            small
            class="btn-add-down"
            :loading="saveLoading"
            @click="saveMetadata"
          >
            <i class="fas fa-save mr-1"></i>
            Salvar configuração do módulo
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "../../services/api";
import toast from "../../lib/ToastNotification";

// ─── Definição dos documentos trabalhistas por colaborador ────────────────────
const DOCUMENT_DEFINITIONS = [
  {
    key: "admission_exam",
    label: "Exame admissional",
    hasValidity: false,
    statusOptions: ["Possui", "Pendente", "Dispensado"],
  },
  {
    key: "periodic_exam",
    label: "Exame periódico",
    hasValidity: true,
    statusOptions: ["Vigente", "Vencido", "Pendente", "Dispensado"],
  },
  {
    key: "dismissal_exam",
    label: "Exame demissional",
    hasValidity: false,
    statusOptions: ["Possui", "Não aplicável", "Pendente"],
  },
  {
    key: "training_nr",
    label: "Treinamento NR-32",
    hasValidity: true,
    statusOptions: ["Vigente", "Vencido", "Pendente"],
  },
  {
    key: "vaccination",
    label: "Carteira de vacinação",
    hasValidity: true,
    statusOptions: ["Atualizada", "Desatualizada", "Pendente"],
  },
  {
    key: "work_card",
    label: "CTPS / Contrato de trabalho",
    hasValidity: false,
    statusOptions: ["Registrado", "Pendente"],
  },
];

const emptyDocData = () => ({
  status: null,
  validity_date: null,
  file_url: null,
  file_name: null,
  _uploading: false,
});

export default {
  name: "DossierHRClt",

  props: {
    mode: {
      type: String,
      default: "edit",
      validator: (v) => ["edit", "view"].includes(v),
    },
    initialData: { type: Object, default: () => ({}) },
    startCollapsed: { type: Boolean, default: false },
  },

  data() {
    const d = this.initialData || {};
    return {
      isCollapsed: this.startCollapsed,
      loadingCollaborators: false,
      saveLoading: false,

      // Lista de colaboradores vinda da API de usuários
      collaborators: [],

      // Dados de documentos por colaborador: { [user_id]: { [doc_key]: { status, validity_date, file_url } } }
      docsData: {},

      // Observações e extras por colaborador: { [user_id]: { observations } }
      extrasData: {},

      // IDs expandidos na lista
      expandedIds: [],

      // IDs sendo salvos individualmente
      savingIds: [],

      // Filtros
      search: "",
      filterRole: null,
      roleOptions: [],

      documentDefinitions: DOCUMENT_DEFINITIONS,

      formData: {
        no_clt_employees: d.no_clt_employees || false,
      },
    };
  },

  computed: {
    isViewMode() {
      return this.mode === "view";
    },

    filteredCollaborators() {
      let list = this.collaborators;

      if (this.search) {
        const q = this.search.toLowerCase();
        list = list.filter(
          (c) =>
            c.name.toLowerCase().includes(q) ||
            (c.cpf || "").includes(q) ||
            (c.office || "").toLowerCase().includes(q)
        );
      }

      if (this.filterRole) {
        list = list.filter((c) => c.office === this.filterRole);
      }

      return list;
    },

    collaboratorSummary() {
      const n = this.collaborators.length;
      if (!n) return null;
      return `${n} colaborador${n !== 1 ? "es" : ""} CLT`;
    },

    // Estado geral do header
    headerState() {
      if (this.formData.no_clt_employees) return "na";
      if (!this.collaborators.length) return "pending";

      const allComplete = this.collaborators.every(
        (c) => this.docProgressPct(c.id) === 100
      );
      return allComplete ? "ok" : "pending";
    },

    headerStateClass() {
      const map = { ok: "--ok", pending: "--pending", na: "--na" };
      return `dossier-hr__state-icon${map[this.headerState] || "--pending"}`;
    },

    headerStateIcon() {
      return (
        {
          ok: "fas fa-check-circle",
          pending: "far fa-circle",
          na: "fas fa-minus-circle",
        }[this.headerState] || "far fa-circle"
      );
    },
  },

  created() {
    this.loadCollaborators();
    if (this.initialData?.docs_data) this.docsData = this.initialData.docs_data;
    if (this.initialData?.extras_data)
      this.extrasData = this.initialData.extras_data;
  },

  methods: {
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
    },

    toggleExpand(id) {
      const idx = this.expandedIds.indexOf(id);
      if (idx === -1) this.expandedIds.push(id);
      else this.expandedIds.splice(idx, 1);
    },

    // ── Carrega colaboradores do cadastro de usuários ──────────────────────
    async loadCollaborators() {
      try {
        this.loadingCollaborators = true;

        const response = await api.get("/rest-home/users/paginate", {
          params: { status: "ATIVO" },
        });

        const { error, users } = response.data;

        if (!error && users) {
          // Filtra apenas usuários que não são dashboard_monitoring
          this.collaborators = users.filter((u) => !u.dashboard_monitoring);

          // Popula opções de cargo/função para o filtro
          const roles = [
            ...new Set(users.map((u) => u.office).filter(Boolean)),
          ].sort();
          this.roleOptions = roles;

          // Inicializa estrutura de docs para colaboradores novos
          this.collaborators.forEach((c) => {
            if (!this.docsData[c.id]) {
              this.docsData[c.id] = {};
            }
            DOCUMENT_DEFINITIONS.forEach((doc) => {
              if (!this.docsData[c.id][doc.key]) {
                this.docsData[c.id][doc.key] = emptyDocData();
              }
            });
            if (!this.extrasData[c.id]) {
              this.extrasData[c.id] = { observations: "" };
            }
          });
        }
      } catch (err) {
        console.error("DossierHRClt.loadCollaborators:", err);
      } finally {
        this.loadingCollaborators = false;
      }
    },

    // ── Acesso reativo aos dados de documento ──────────────────────────────
    // Usa $set para garantir reatividade no Vue 2
    getDocData(userId, docKey) {
      if (!this.docsData[userId]) {
        this.$set(this.docsData, userId, {});
      }
      if (!this.docsData[userId][docKey]) {
        this.$set(this.docsData[userId], docKey, emptyDocData());
      }
      return this.docsData[userId][docKey];
    },

    getCollaboratorExtra(userId) {
      if (!this.extrasData[userId]) {
        this.$set(this.extrasData, userId, { observations: "" });
      }
      return this.extrasData[userId];
    },

    // ── Progresso de documentos por colaborador ────────────────────────────
    docProgress(userId) {
      const docs = this.docsData[userId] || {};
      const filled = DOCUMENT_DEFINITIONS.filter(
        (d) => docs[d.key]?.status
      ).length;
      return `${filled}/${DOCUMENT_DEFINITIONS.length} docs`;
    },

    docProgressPct(userId) {
      const docs = this.docsData[userId] || {};
      const filled = DOCUMENT_DEFINITIONS.filter(
        (d) => docs[d.key]?.status
      ).length;
      return Math.round((filled / DOCUMENT_DEFINITIONS.length) * 100);
    },

    progressClass(userId) {
      const pct = this.docProgressPct(userId);
      if (pct === 100) return "dossier-hr__progress-fill--ok";
      if (pct >= 50) return "dossier-hr__progress-fill--partial";
      return "dossier-hr__progress-fill--low";
    },

    // ── Estado por item e por doc ──────────────────────────────────────────
    itemStateClass(userId) {
      const pct = this.docProgressPct(userId);
      if (pct === 100) return "dossier-hr__item-state--ok";
      if (pct > 0) return "dossier-hr__item-state--partial";
      return "dossier-hr__item-state--pending";
    },

    itemStateIcon(userId) {
      const pct = this.docProgressPct(userId);
      if (pct === 100) return "fas fa-check-circle";
      if (pct > 0) return "fas fa-adjust";
      return "far fa-circle";
    },

    docCardClass(userId, docKey) {
      const data = this.getDocData(userId, docKey);
      if (!data.status) return "dossier-hr__doc-card--pending";
      const bad = ["Vencido", "Desatualizada", "Pendente"];
      return bad.includes(data.status)
        ? "dossier-hr__doc-card--warning"
        : "dossier-hr__doc-card--ok";
    },

    docBadgeClass(userId, docKey) {
      const data = this.getDocData(userId, docKey);
      const bad = ["Vencido", "Desatualizada", "Pendente"];
      const good = ["Possui", "Vigente", "Atualizada", "Registrado"];
      if (!data.status) return "dossier-hr__badge--pending";
      if (bad.includes(data.status)) return "dossier-hr__badge--warning";
      if (good.includes(data.status)) return "dossier-hr__badge--ok";
      return "dossier-hr__badge--neutral";
    },

    docStateBadge(userId, docKey) {
      return this.getDocData(userId, docKey).status || "Não informado";
    },

    // ── Upload por documento ───────────────────────────────────────────────
    triggerDocUpload(userId, docKey) {
      const refKey = `upload_${userId}_${docKey}`;
      const ref = this.$refs[refKey];
      if (!ref) return;
      const el = Array.isArray(ref) ? ref[0] : ref;
      if (el) el.click();
    },

    async handleDocUpload(event, userId, docKey) {
      const file = event.target.files[0];
      if (!file) return;

      if (file.type !== "application/pdf") {
        toast("Apenas PDFs são aceitos.", "error");
        return;
      }

      this.$set(this.docsData[userId][docKey], "_uploading", true);

      try {
        const formData = new FormData();
        formData.append("file", file, file.name);
        formData.append("user_id", userId);
        formData.append("doc_key", docKey);

        const response = await api.post(
          "/rest-home/dossier/hr/upload",
          formData
        );
        const { error, file_url, file_name, message } = response.data;

        if (!error) {
          this.$set(this.docsData[userId][docKey], "file_url", file_url);
          this.$set(
            this.docsData[userId][docKey],
            "file_name",
            file_name || file.name
          );
          toast(message || "Arquivo anexado com sucesso", "success");
        }
      } catch (err) {
        toast("Erro ao enviar arquivo.", "error");
        console.error("DossierHRClt.upload:", err);
      } finally {
        this.$set(this.docsData[userId][docKey], "_uploading", false);
        event.target.value = "";
      }
    },

    // ── Salvar colaborador individual ──────────────────────────────────────
    async saveCollaborator(userId) {
      try {
        this.savingIds.push(userId);

        // Remove campos _ antes de enviar
        const docs = {};
        Object.keys(this.docsData[userId] || {}).forEach((key) => {
          const d = this.docsData[userId][key];
          docs[key] = {
            status: d.status,
            validity_date: d.validity_date,
            file_url: d.file_url,
            file_name: d.file_name,
          };
        });

        const response = await api.post("/rest-home/dossier/hr/collaborator", {
          user_id: userId,
          docs,
          observations: this.extrasData[userId]?.observations || "",
        });

        const { error, errors, message } = response.data;

        if (error) {
          this.$emit("error", { module: "hr_clt", errors });
        } else {
          toast(message || "Salvo com sucesso", "success");
          this.$emit("saved", { module: "hr_clt", user_id: userId });
        }
      } catch (err) {
        console.error("DossierHRClt.saveCollaborator:", err);
      } finally {
        this.savingIds = this.savingIds.filter((id) => id !== userId);
      }
    },

    // ── Salvar metadados do módulo (flag no_clt) ───────────────────────────
    async saveMetadata() {
      try {
        this.saveLoading = true;
        const response = await api.post("/rest-home/dossier/hr/metadata", {
          no_clt_employees: this.formData.no_clt_employees,
        });
        const { error, message } = response.data;
        if (!error) {
          toast(message || "Configuração salva", "success");
          this.$emit("saved", { module: "hr_clt", metadata: true });
        }
      } catch (err) {
        console.error("DossierHRClt.saveMetadata:", err);
      } finally {
        this.saveLoading = false;
      }
    },

    // ── Utilitários ────────────────────────────────────────────────────────
    initials(name) {
      if (!name) return "?";
      return name
        .split(" ")
        .slice(0, 2)
        .map((n) => n[0])
        .join("")
        .toUpperCase();
    },

    formatDateBR(dateStr) {
      if (!dateStr) return "—";
      const [y, m, d] = dateStr.split("-");
      return `${d}/${m}/${y}`;
    },
  },
};
</script>

<style scoped>
/* ═══════════════════════════════════════════════════════════════
   DOSSIER RH – CLT
   ═══════════════════════════════════════════════════════════════ */

.dossier-hr__wrapper {
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 12px;
  transition: box-shadow 0.2s;
}

.dossier-hr__wrapper:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

/* ── Cabeçalho ───────────────────────────────────────────────── */
.dossier-hr__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f8f8f7;
  border-bottom: 1px solid #e8e8e8;
  cursor: pointer;
  user-select: none;
}

.dossier-hr__header:hover {
  background: #f0f0ef;
}

.dossier-hr__header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.dossier-hr__title {
  font-size: 14px;
  font-weight: 600;
  color: #33495e;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.dossier-hr__subtitle {
  font-size: 12px;
  color: #888;
  display: block;
  margin-top: 2px;
}

.dossier-hr__state-icon {
  font-size: 15px;
  width: 20px;
  text-align: center;
  flex-shrink: 0;
}

.dossier-hr__state-icon--ok {
  color: #30915b;
}
.dossier-hr__state-icon--pending {
  color: #ccc;
}
.dossier-hr__state-icon--na {
  color: #bbb;
}

.dossier-hr__toggle {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #888;
  font-size: 13px;
}

.dossier-hr__toggle:hover {
  color: #1f5a76;
}

/* ── Corpo ───────────────────────────────────────────────────── */
.dossier-hr__body {
  padding: 16px;
}

.dossier-hr__explainer {
  font-size: 13px;
  color: #555;
  background: #f8f8f7;
  border-left: 3px solid #1f5a76;
  border-radius: 0 4px 4px 0;
  padding: 10px 12px;
  margin-bottom: 14px;
  line-height: 1.6;
}

.dossier-hr__explainer p {
  margin: 0;
}

/* ── Toolbar ─────────────────────────────────────────────────── */
.dossier-hr__toolbar {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 14px;
}

.dossier-hr__search {
  flex: 1;
  min-width: 180px;
}
.dossier-hr__filter-role {
  flex: 0 1 200px;
}

/* ── Lista de colaboradores ──────────────────────────────────── */
.dossier-hr__item {
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  margin-bottom: 6px;
  overflow: hidden;
}

.dossier-hr__item--expanded {
  border-color: #b8d9ec;
}

.dossier-hr__item-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  cursor: pointer;
  background: #fff;
  transition: background 0.15s;
}

.dossier-hr__item-row:hover {
  background: #fafafa;
}

/* ── Avatar ──────────────────────────────────────────────────── */
.dossier-hr__item-avatar {
  flex-shrink: 0;
}

.dossier-hr__avatar-img {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  object-fit: cover;
}

.dossier-hr__avatar-initials {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #1f5a76;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ── Info do item ────────────────────────────────────────────── */
.dossier-hr__item-state {
  font-size: 14px;
  flex-shrink: 0;
  width: 18px;
  text-align: center;
}

.dossier-hr__item-state--ok {
  color: #30915b;
}
.dossier-hr__item-state--partial {
  color: #fb8b01;
}
.dossier-hr__item-state--pending {
  color: #ccc;
}

.dossier-hr__item-info {
  flex: 1;
  min-width: 0;
}

.dossier-hr__item-name {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dossier-hr__item-meta {
  display: block;
  font-size: 11px;
  color: #888;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-top: 1px;
}

/* ── Progresso ───────────────────────────────────────────────── */
.dossier-hr__item-progress {
  flex-shrink: 0;
  width: 120px;
}

.dossier-hr__progress-text {
  font-size: 11px;
  color: #888;
  display: block;
  margin-bottom: 3px;
}

.dossier-hr__progress-bar {
  height: 4px;
  background: #eee;
  border-radius: 2px;
  overflow: hidden;
}

.dossier-hr__progress-fill {
  height: 100%;
  border-radius: 2px;
  transition: width 0.3s;
}

.dossier-hr__progress-fill--ok {
  background: #30915b;
}
.dossier-hr__progress-fill--partial {
  background: #fb8b01;
}
.dossier-hr__progress-fill--low {
  background: #dc3545;
}

.dossier-hr__item-chevron {
  font-size: 11px;
  color: #ccc;
  flex-shrink: 0;
}

/* ── Painel de documentos ────────────────────────────────────── */
.dossier-hr__item-docs {
  padding: 12px;
  background: #fafafa;
  border-top: 1px solid #e8e8e8;
}

.dossier-hr__docs-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 10px;
  margin-bottom: 12px;
}

/* ── Card de documento individual ────────────────────────────── */
.dossier-hr__doc-card {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 10px;
}

.dossier-hr__doc-card--ok {
  border-left: 3px solid #30915b;
}
.dossier-hr__doc-card--warning {
  border-left: 3px solid #fb8b01;
}
.dossier-hr__doc-card--pending {
  border-left: 3px solid #e0e0e0;
}

.dossier-hr__doc-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  gap: 6px;
}

.dossier-hr__doc-name {
  font-size: 11px;
  font-weight: 600;
  color: #444;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  flex: 1;
}

/* ── Badges de estado do doc ─────────────────────────────────── */
.dossier-hr__doc-state-badge {
  font-size: 10px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 8px;
  text-transform: uppercase;
  letter-spacing: 0.2px;
  flex-shrink: 0;
}

.dossier-hr__badge--ok {
  background: #d4edda;
  color: #155724;
}
.dossier-hr__badge--warning {
  background: #fff3cd;
  color: #856404;
}
.dossier-hr__badge--pending {
  background: #f0f0f0;
  color: #888;
}
.dossier-hr__badge--neutral {
  background: #e8f4fd;
  color: #1f5a76;
}

/* ── Campos do doc ───────────────────────────────────────────── */
.dossier-hr__doc-field {
  margin-bottom: 6px;
}

.dossier-hr__doc-label {
  display: block;
  font-size: 10px;
  font-weight: 600;
  color: #777;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 3px;
}

.dossier-hr__doc-select {
  font-size: 12px;
}
.dossier-hr__doc-input {
  font-size: 12px;
  max-width: 100%;
}

.dossier-hr__doc-view-status {
  font-size: 12px;
  color: #555;
  padding: 2px 0;
}
.dossier-hr__doc-view-value {
  font-size: 12px;
  color: #333;
}
.dossier-hr__doc-empty {
  font-size: 11px;
  color: #aaa;
  font-style: italic;
}

.dossier-hr__doc-file-link {
  font-size: 12px;
  color: #1f5a76;
  font-weight: 600;
}

/* ── Observações ─────────────────────────────────────────────── */
.dossier-hr__doc-obs {
  margin-top: 10px;
}

/* ── Ações por item ──────────────────────────────────────────── */
.dossier-hr__item-save {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid #ebebeb;
}

/* ── Footer do módulo ────────────────────────────────────────── */
.dossier-hr__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #f0f0ef;
}

.dossier-hr__na-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #555;
  cursor: pointer;
}

.dossier-hr__na-checkbox {
  accent-color: #1f5a76;
  width: 15px;
  height: 15px;
  cursor: pointer;
}

/* ── Estado vazio ────────────────────────────────────────────── */
.dossier-hr__empty {
  text-align: center;
  padding: 32px 16px;
  color: #888;
}

.dossier-hr__empty-icon {
  font-size: 36px;
  display: block;
  color: #ddd;
  margin-bottom: 10px;
}

.dossier-hr__empty-sub {
  font-size: 12px;
  margin-top: 4px;
}

/* ── Responsivo ──────────────────────────────────────────────── */
@media (max-width: 600px) {
  .dossier-hr__body {
    padding: 12px;
  }

  .dossier-hr__toolbar {
    flex-direction: column;
    align-items: stretch;
  }
  .dossier-hr__search,
  .dossier-hr__filter-role {
    flex: unset;
    width: 100%;
  }

  .dossier-hr__docs-grid {
    grid-template-columns: 1fr;
  }

  .dossier-hr__item-progress {
    display: none;
  }

  .dossier-hr__footer {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>